import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import {
  ToggleRadioButtonGroup,
  type ToggleRadioButtonGroupProps,
} from '~/components/elements/ToggleRadioButtonGroup'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  type ResultViewerType,
  resultViewerTypeLabelI18nKeyMap,
  resultViewerTypes,
} from '~/utils'

type Attribute =
  ToggleRadioButtonGroupProps<ResultViewerType>['attributes'][number]

export type ResultViewerSwitcherInnerProps = {
  'data-testid'?: string
  disabled?: boolean
  onChange?: (value: ResultViewerType) => void
  value: ResultViewerType
}

export const ResultViewerSwitcherInner = ({
  'data-testid': dataTestId,
  disabled,
  onChange,
  value,
}: ResultViewerSwitcherInnerProps) => {
  const logEvent = useLogEvent()
  const { t } = useTranslation()

  const attributes: Attribute[] = React.useMemo(() => {
    const attributeMap = R.mapToObj(resultViewerTypes, (resultViewerType) => [
      resultViewerType,
      {
        text: t(resultViewerTypeLabelI18nKeyMap[resultViewerType]),
        value: resultViewerType,
      } satisfies Attribute,
    ])

    return [attributeMap.sequential, attributeMap.individual]
  }, [t])

  const handleChange = React.useCallback<
    EH<ToggleRadioButtonGroupProps<ResultViewerType>, 'onChange'>
  >(
    (resultViewerType) => {
      onChange?.(resultViewerType)

      logEvent(
        {
          individual: 'Show each question and graph',
          sequential: 'Show consecutive questions',
        }[resultViewerType],
      )
    },
    [logEvent, onChange],
  )

  return (
    <ToggleRadioButtonGroup
      attributes={attributes}
      data-testid={dataTestId}
      disabled={disabled}
      value={value}
      onChange={handleChange}
    />
  )
}
